import React, { memo, useCallback, useMemo } from "react";
import moment from "moment";
import { HiOutlineChevronRight } from "react-icons/hi";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import {
  baseURLImage,
  capitalizeWords,
  formatAmount,
  getNameProfile,
  parseBooleanVal,
  timeToQuantity,
} from "Utils/utils";
import {
  resolveShiftListAmount,
  resolveShiftListDate,
  resolveShiftListDuration,
} from "../../UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";
import CircularLoader from "../../../../components/common/CircularLoader/CircularLoader";
import TimesheetStatusCell from "../components/TimesheetStatusCell";

const resolveWorkers = (item) => {
  if (Array.isArray(item?.workers) && item.workers.length > 0) {
    return item.workers;
  }
  if (item?.employee_profile) {
    return [item.employee_profile];
  }
  return [];
};

const isAdvanceShift = (item) =>
  Boolean(
    item?.billing_mode === "advance" ||
      item?.is_v5_multi_assignment === true ||
      item?.is_multi_assignment === true
  );

const isSplitShift = (item) =>
  !isAdvanceShift(item) &&
  Boolean(item?.is_split_shift || item?.billing_mode === "split");

const resolveServiceType = (item) => {
  if (isAdvanceShift(item)) {
    return "Advance shift";
  }
  if (item?.is_split_shift || item?.billing_mode === "split") {
    return "This is split shift";
  }
  return item?.invoice_service_type ?? "—";
};

const isStaShift = (item) => {
  const value = item?.is_sta ?? item?.sta;
  return parseBooleanVal(value) || value === 1 || String(value) === "1";
};

const TimesheetTable = ({ item, selected, onSelect, isLoading }) => {
  const rowKey = item?.shift_uuid ?? item?.uuid ?? item?.id;
  const workers = useMemo(() => resolveWorkers(item), [item]);
  const primaryWorker = workers[0];
  const serviceType = useMemo(() => resolveServiceType(item), [item]);
  const advanceShift = useMemo(() => isAdvanceShift(item), [item]);
  const splitShift = useMemo(() => isSplitShift(item), [item]);
  const staShift = useMemo(() => isStaShift(item), [item]);

  const shiftDate = useMemo(() => resolveShiftListDate(item), [item]);
  const formattedShiftDate = useMemo(
    () => (shiftDate ? moment(shiftDate).format("ddd, D MMM YY") : "—"),
    [shiftDate]
  );

  const durationQuantity = useMemo(
    () => timeToQuantity(resolveShiftListDuration(item)),
    [item]
  );

  const totalAmount = useMemo(() => resolveShiftListAmount(item), [item]);
  const formattedTotalAmount = useMemo(() => {
    if (!totalAmount) return null;
    return `$${formatAmount(parseFloat(totalAmount).toFixed(2))}`;
  }, [totalAmount]);

  const workerAvatarData = useMemo(
    () =>
      workers.map((worker) => ({
        first_name: worker?.first_name,
        last_name: worker?.last_name,
        photo: worker?.photo,
      })),
    [workers]
  );

  const handleImageError = useCallback((e, fname, lname) => {
    e.target.src = getNameProfile(fname, lname);
  }, []);

  const handleRowClick = useCallback(() => {
    onSelect(rowKey);
  }, [onSelect, rowKey]);

  if (isLoading) {
    return (
      <tr className="bs-loading-row">
        <td colSpan={8} className="text-center">
          <CircularLoader classes="text-sm" width_size="w-12" />
        </td>
      </tr>
    );
  }

  return (
    <tr
      onClick={handleRowClick}
      className={selected ? "is-selected" : undefined}
      aria-selected={selected}
    >
      <td>
        <HiOutlineChevronRight className="bs-chevron" aria-hidden />
      </td>
      <td>
        <div className="bs-participant-cell">
          {item?.participants && item.participants.length === 1 ? (
            <span>
              {`${capitalizeWords(item.participants[0]?.first_name)} ${capitalizeWords(item.participants[0]?.last_name)}`}
            </span>
          ) : item?.participants?.length > 1 ? (
            <AvatarGroup
              arrayData={item?.participants}
              cssClass="mt-0"
              maxLength={10}
              isNoRing={true}
            />
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </div>
      </td>
      <td className="bs-td-worker capitalize">
        <div className="bs-worker-cell">
          {workers.length > 1 ? (
            <AvatarGroup
              arrayData={workerAvatarData}
              cssClass="mt-0"
              maxLength={5}
              isNoRing={true}
            />
          ) : primaryWorker ? (
            <>
              <img
                onError={(e) =>
                  handleImageError(
                    e,
                    primaryWorker?.first_name,
                    primaryWorker?.last_name
                  )
                }
                className="bs-worker-avatar capitalize"
                src={
                  primaryWorker?.photo
                    ? baseURLImage(primaryWorker.photo)
                    : getNameProfile(
                        primaryWorker?.first_name,
                        primaryWorker?.last_name || " "
                      )
                }
                alt={`${primaryWorker?.first_name || ""} ${primaryWorker?.last_name || ""}`}
              />
              <span
                className="bs-worker-name"
                title={`${primaryWorker?.first_name || ""} ${primaryWorker?.last_name || ""}`.trim()}
              >
                {primaryWorker?.first_name} {primaryWorker?.last_name}
              </span>
            </>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </div>
      </td>
      <TimesheetStatusCell item={item} tdClassName="bs-cell-center" />
      <td className="bs-cell-center">
        <span className="bs-date">{formattedShiftDate}</span>
      </td>
      <td className="bs-cell-center">
        <div className="bs-service-cell">
          {advanceShift ? (
            <span className="bs-badge bs-badge--advance" title="Advance shift">
              Advance
            </span>
          ) : splitShift ? (
            <span className="bs-badge bs-badge--split" title={serviceType}>
              Split shift
            </span>
          ) : serviceType !== "—" ? (
            <span className="bs-badge bs-badge--service" title={serviceType}>
              {serviceType}
            </span>
          ) : !staShift ? (
            "—"
          ) : null}
          {staShift && (
            <span
              className="bs-badge bs-badge--sta"
              title="Short Term Accommodation (STA) shift"
            >
              STA
            </span>
          )}
        </div>
      </td>
      <td className="bs-cell-center">
        <span className="bs-quantity">{durationQuantity}</span>
      </td>
      <td className="bs-cell-center">
        {formattedTotalAmount ? (
          <span className="bs-amount">{formattedTotalAmount}</span>
        ) : (
          <span className="bs-amount is-na">N/A</span>
        )}
      </td>
    </tr>
  );
};

export default memo(TimesheetTable);
